import {
  type ChatAttachmentResponse,
  deleteChatAttachment,
  uploadChatAttachment,
} from '@/pages/api/scribeAPIV2';
import { logger } from '@/utils/logger/logger';
import { useMutation } from '@tanstack/react-query';

const UPLOAD_CHAT_ATTACHMENT = 'uploadChatAttachment';
const DELETE_CHAT_ATTACHMENT = 'deleteChatAttachment';

// Map to track active upload AbortControllers by file uid
const activeUploadControllers = new Map<string, AbortController>();

/**
 * Cancel an active chat attachment upload by file uid.
 * @returns true if an upload was cancelled, false otherwise
 */
export const cancelChatAttachmentUpload = (uid: string): boolean => {
  const controller = activeUploadControllers.get(uid);
  if (controller) {
    controller.abort();
    activeUploadControllers.delete(uid);
    return true;
  }
  return false;
};

interface UploadChatAttachmentVariables {
  uid: string;
  file: File;
  chatId?: string;
  onProgressUpdate?: (progress: number) => void;
}

/**
 * Hook for uploading a single chat attachment.
 * Supports progress tracking and cancellation via AbortController.
 */
export function useMutateUploadChatAttachment() {
  return useMutation({
    mutationKey: [UPLOAD_CHAT_ATTACHMENT],
    mutationFn: async ({
      uid,
      file,
      chatId,
      onProgressUpdate,
    }: UploadChatAttachmentVariables): Promise<ChatAttachmentResponse> => {
      // Create AbortController for cancellation support
      const controller = new AbortController();
      activeUploadControllers.set(uid, controller);

      try {
        const response = await uploadChatAttachment({
          file,
          chatId,
          onProgressUpdate,
          signal: controller.signal,
        });
        return response.data;
      } finally {
        activeUploadControllers.delete(uid);
      }
    },
    onError: (error: Error, variables) => {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        logger.debug(`Chat attachment upload cancelled for ${variables.file.name}`);
      } else {
        logger.error(`Error uploading chat attachment: ${error.message}`, {
          fileName: variables.file.name,
          fileSize: variables.file.size,
        });
      }
    },
  });
}

/**
 * Hook for deleting a chat attachment.
 */
export function useMutateDeleteChatAttachment() {
  return useMutation({
    mutationKey: [DELETE_CHAT_ATTACHMENT],
    mutationFn: async (attachmentId: string): Promise<void> => {
      await deleteChatAttachment(attachmentId);
    },
    onError: (error: Error, attachmentId) => {
      logger.error(`Error deleting chat attachment: ${error.message}`, {
        attachmentId,
      });
    },
  });
}
